import { APIRoutes } from "@/api";
import { Channel, messageSchema } from "@/controllers/channel/schema";
import { ChatMessagesLatestMessageQuery } from "@/hooks/queries/chat/messages/use-chat-messages-latest-message-query";
import { ChatChannelsQuery } from "@/hooks/queries/chat/use-channels-query";
import { queryClient } from "@/lib/query-client";
import { ApiService } from "@/services/api/api-service";

import {
  MutationOptions,
  useMutation as useMutationTanstack,
} from "@tanstack/react-query";

type TVariables = {
  userId: string;
  channelId: string;
  messageIds: string[];
  isRead: boolean;
};

async function updateMessageStatus({
  channelId,
  messageIds: messageIds,
  isRead,
}: TVariables) {
  const payload = {
    messageIds,
    isRead,
  };
  const data = await fetch(APIRoutes.CHAT_MESSAGES(channelId), {
    method: "PATCH",
    body: JSON.stringify(payload),
  }).then((res) => res.json());

  const result = ApiService.parseApiResponse(data, messageSchema);

  if (result.status !== "success") {
    return null;
  }

  return result.data;
}

type TData = Awaited<ReturnType<typeof updateMessageStatus>>;
type TContext = {
  previousLatestMessage: TData | null;
  previousChannel: Channel | null;
};

export namespace UpdateMessageStatusMutation {
  export function useMutation(
    options?: MutationOptions<TData, unknown, TVariables, TContext>,
  ) {
    return useMutationTanstack<TData, unknown, TVariables, TContext>({
      mutationFn: updateMessageStatus,
      ...options,

      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: ChatMessagesLatestMessageQuery.keys.all,
        });

        const previousLatestMessage = ChatMessagesLatestMessageQuery.getData(
          variables.channelId,
        );

        if (!previousLatestMessage) {
          return {
            previousLatestMessage: null,
            previousChannel: null,
          };
        }

        const updater = ChatMessagesLatestMessageQuery.updaters.updateMessage({
          ...previousLatestMessage,
          readBy: previousLatestMessage.readBy.map((r) =>
            r.userId === variables.userId
              ? { ...r, readAt: variables.isRead ? new Date() : null }
              : r,
          ),
        });
        ChatMessagesLatestMessageQuery.updateData(variables.channelId, updater);

        const previousChannel =
          ChatChannelsQuery.getData()
            ?.pages.flat()
            .find((c) => c.id === variables.channelId) ?? null;

        if (previousChannel) {
          const channelUpdater = ChatChannelsQuery.updaters.updateChannel({
            ...previousChannel,
            messages: previousChannel.messages.map((m) =>
              variables.messageIds.includes(m.id)
                ? { ...m, isRead: variables.isRead }
                : m,
            ),
          });
          ChatChannelsQuery.updateData(channelUpdater);
        }

        return { previousLatestMessage, previousChannel };
      },

      onError: (_error, variables, context) => {
        if (!context) {
          return;
        }
        if (context.previousLatestMessage) {
          ChatMessagesLatestMessageQuery.setData(
            variables.channelId,
            context.previousLatestMessage,
          );
        }

        if (context.previousChannel) {
          const channelUpdater = ChatChannelsQuery.updaters.updateChannel(
            context.previousChannel,
          );
          ChatChannelsQuery.updateData(channelUpdater);
        }
      },

      onSettled: (_data, err, variables) => {
        ChatMessagesLatestMessageQuery.invalidate(
          ChatMessagesLatestMessageQuery.keys.channel(variables.channelId),
        );

        ChatChannelsQuery.invalidate();
      },
    });
  }
}
